import { Platform } from 'react-native';
import * as SecureStore from 'expo-secure-store';
import type {
  UaePassClientType,
  UaePassData,
  UaePassFlowResult,
} from '@tabbio/types';
import type { SupportedLocale } from '../hooks/useLocale';
import {
  normalizePostAuthTarget,
  type PostAuthTarget,
} from './auth-handoff';
import { readWebStorage, removeWebStorage, writeWebStorage } from './web-storage';

const UAE_PASS_AUTH_CONTEXT_KEY = 'uae_pass_auth_context';
const UAE_PASS_PENDING_RESULT_KEY = 'uae_pass_pending_result';
const UAE_PASS_AUTH_RESULT_KEY = 'uae_pass_auth_result';
const UAE_PASS_CALLBACK_FINGERPRINT_KEY = 'uae_pass_callback_fingerprint';
const UAE_PASS_POST_AUTH_TARGET_KEY = 'uae_pass_post_auth_target';

let uaePassNativeAuthSheetCleanupPending = false;

export type UaePassMode = 'sign_in' | 'settings_link';
export type UaePassPostAuthTarget = Exclude<PostAuthTarget, 'root'>;

type UaePassAuthContext = {
  authorizeUrl?: string;
  clientType: UaePassClientType;
  createdAt?: number;
  locale?: SupportedLocale;
  mode: UaePassMode;
  redirectUri: string;
  state: string;
};

type UaePassPendingResult = Extract<
  UaePassFlowResult,
  { status: 'manual_linking_required' }
>;

type UaePassAuthenticatedResult = Extract<
  UaePassFlowResult,
  { status: 'authenticated' }
>;

function readStorage(key: string) {
  if (Platform.OS === 'web') {
    return readWebStorage(key, 'session');
  }

  return SecureStore.getItem(key);
}

function writeStorage(key: string, value: string) {
  if (Platform.OS === 'web') {
    writeWebStorage(key, value, 'session');
    return;
  }

  SecureStore.setItem(key, value);
}

function removeStorage(key: string) {
  if (Platform.OS === 'web') {
    removeWebStorage(key, 'session');
    return;
  }

  void SecureStore.deleteItemAsync(key);
}

function parseJSON<T>(value: string | null) {
  if (!value) {
    return null;
  }

  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
}

function getUaePassAuthContextStateKey(state: string) {
  const safeState = state.replace(/[^A-Za-z0-9._-]/g, '_');
  return `${UAE_PASS_AUTH_CONTEXT_KEY}.${safeState}`;
}

export function saveUaePassAuthContext(value: UaePassAuthContext) {
  const serialized = JSON.stringify(value);
  writeStorage(UAE_PASS_AUTH_CONTEXT_KEY, serialized);
  writeStorage(getUaePassAuthContextStateKey(value.state), serialized);
}

export function readUaePassAuthContext(state?: string | null) {
  if (state) {
    const stateContext = parseJSON<UaePassAuthContext>(
      readStorage(getUaePassAuthContextStateKey(state)),
    );

    if (stateContext) {
      return stateContext;
    }

    const currentContext = parseJSON<UaePassAuthContext>(
      readStorage(UAE_PASS_AUTH_CONTEXT_KEY),
    );
    return currentContext?.state === state ? currentContext : null;
  }

  return parseJSON<UaePassAuthContext>(readStorage(UAE_PASS_AUTH_CONTEXT_KEY));
}

export function clearUaePassAuthContext(state?: string | null) {
  if (state) {
    removeStorage(getUaePassAuthContextStateKey(state));

    const currentContext = readUaePassAuthContext();
    if (currentContext?.state === state) {
      removeStorage(UAE_PASS_AUTH_CONTEXT_KEY);
    }
    return;
  }

  const currentContext = readUaePassAuthContext();
  if (currentContext?.state) {
    removeStorage(getUaePassAuthContextStateKey(currentContext.state));
  }
  removeStorage(UAE_PASS_AUTH_CONTEXT_KEY);
}

export function saveUaePassPendingResult(value: UaePassPendingResult) {
  writeStorage(UAE_PASS_PENDING_RESULT_KEY, JSON.stringify(value));
}

export function readUaePassPendingResult() {
  return parseJSON<UaePassPendingResult>(readStorage(UAE_PASS_PENDING_RESULT_KEY));
}

export function clearUaePassPendingResult() {
  removeStorage(UAE_PASS_PENDING_RESULT_KEY);
}

export function saveUaePassAuthenticatedResult(value: UaePassAuthenticatedResult) {
  writeStorage(UAE_PASS_AUTH_RESULT_KEY, JSON.stringify(value));
}

export function readUaePassAuthenticatedResult() {
  return parseJSON<UaePassAuthenticatedResult>(readStorage(UAE_PASS_AUTH_RESULT_KEY));
}

export function clearUaePassAuthenticatedResult() {
  removeStorage(UAE_PASS_AUTH_RESULT_KEY);
}

function normalizeStoredPostAuthTarget(
  value: string | null,
): UaePassPostAuthTarget | null {
  const target = normalizePostAuthTarget(value ?? undefined);
  return target === 'root' ? null : target;
}

export function saveUaePassPostAuthTarget(value: UaePassPostAuthTarget) {
  writeStorage(UAE_PASS_POST_AUTH_TARGET_KEY, value);
}

export function readUaePassPostAuthTarget() {
  return normalizeStoredPostAuthTarget(readStorage(UAE_PASS_POST_AUTH_TARGET_KEY));
}

export function consumeUaePassPostAuthTarget() {
  const target = readUaePassPostAuthTarget();
  if (target) {
    removeStorage(UAE_PASS_POST_AUTH_TARGET_KEY);
  }
  return target;
}

export function clearUaePassPostAuthTarget() {
  removeStorage(UAE_PASS_POST_AUTH_TARGET_KEY);
}

export function markUaePassNativeAuthSheetCleanupPending() {
  if (Platform.OS !== 'web') {
    uaePassNativeAuthSheetCleanupPending = true;
  }
}

export function hasUaePassNativeAuthSheetCleanupPending() {
  return Platform.OS !== 'web' && uaePassNativeAuthSheetCleanupPending;
}

export function consumeUaePassNativeAuthSheetCleanupPending() {
  const pending = hasUaePassNativeAuthSheetCleanupPending();
  uaePassNativeAuthSheetCleanupPending = false;
  return pending;
}

export function getPendingUaePassData(): UaePassData | null {
  return readUaePassPendingResult()?.uaePassData ?? null;
}

export function beginUaePassCallbackProcessing(fingerprint: string) {
  const current = readStorage(UAE_PASS_CALLBACK_FINGERPRINT_KEY);
  if (current === fingerprint) {
    return false;
  }

  writeStorage(UAE_PASS_CALLBACK_FINGERPRINT_KEY, fingerprint);
  return true;
}

export function clearUaePassCallbackProcessing(fingerprint?: string) {
  if (!fingerprint) {
    removeStorage(UAE_PASS_CALLBACK_FINGERPRINT_KEY);
    return;
  }

  const current = readStorage(UAE_PASS_CALLBACK_FINGERPRINT_KEY);
  if (current === fingerprint) {
    removeStorage(UAE_PASS_CALLBACK_FINGERPRINT_KEY);
  }
}
